import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
    LayoutDashboard, 
    FileText, 
    Users, 
    Upload, 
    LogOut,
    FileCheck,
    BarChart3,
    History,
    Activity,
    Menu,
    X,
    RefreshCw,
    Mail
} from 'lucide-react';
import { Landmark, Link2, Copy, Check, CreditCard, Sparkles, Trash2, Wallet } from 'lucide-react';
import { BankCategoriesSection } from './admin/BankCategoriesDialog';
import { NotificationsPanel } from './admin/NotificationsPanel';
import { Button } from './ui/button';
import { useState } from 'react';

// Refresh Button Component
export const RefreshButton = () => {
    const handleRefresh = () => {
        window.location.reload();
    };

    return (
        <Button
            onClick={handleRefresh}
            variant="outline"
            size="sm"
            className="fixed top-20 md:top-4 right-4 z-40 bg-white shadow-lg border-sky-200 hover:bg-sky-50 hover:border-sky-300 transition-all duration-200 rounded-full px-3 py-2"
            data-testid="refresh-page-btn"
            title="Refrescar página"
        >
            <RefreshCw className="w-4 h-4 text-sky-600" />
            <span className="text-sky-700 font-medium ml-2 hidden sm:inline">Refrescar</span>
        </Button>
    );
};

import { createPortal } from 'react-dom';

const ShareLinkModal = ({ link, onClose }) => {
    const [copied, setCopied] = useState(false);

    const handleCopy = () => {
        navigator.clipboard.writeText(link).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    return createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50" onClick={onClose}>
            <div
                className="bg-white rounded-xl shadow-xl w-[90vw] max-w-sm mx-4 overflow-hidden"
                onClick={(e) => e.stopPropagation()}
                data-testid="share-link-modal"
            >
                <div className="bg-violet-50 px-5 py-4 border-b border-violet-100">
                    <h3 className="font-semibold text-violet-800 flex items-center gap-2">
                        <Link2 className="w-5 h-5" /> Enlace de Pago
                    </h3>
                </div>
                <div className="px-5 py-4 space-y-4">
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                        <p className="text-sm font-medium text-amber-800 mb-1">Informacion de Cobro</p>
                        <p className="text-xs text-amber-700">
                            Este enlace usa <span className="font-bold">Stripe</span> como forma de pago.
                            Al monto recibido se le descuenta automaticamente:
                        </p>
                        <ul className="mt-2 text-xs text-amber-800 space-y-1">
                            <li className="flex items-center gap-1">
                                <span className="w-1.5 h-1.5 bg-amber-500 rounded-full shrink-0"></span>
                                <span><strong>2.9%</strong> de comision sobre el monto</span>
                            </li>
                            <li className="flex items-center gap-1">
                                <span className="w-1.5 h-1.5 bg-amber-500 rounded-full shrink-0"></span>
                                <span><strong>$0.30 centavos</strong> por transaccion</span>
                            </li>
                        </ul>
                        <p className="mt-2 text-xs text-amber-600 italic">
                            Ej: $10.00 x 0.971 = $9.71 - $0.30 = <strong>$9.41 neto</strong>
                        </p>
                    </div>

                    <div>
                        <p className="text-xs font-medium text-slate-500 mb-1.5">Tu enlace:</p>
                        <div className="flex items-center gap-2">
                            <div className="flex-1 min-w-0 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs text-slate-600 truncate select-all">
                                {link}
                            </div>
                            <button
                                onClick={handleCopy}
                                className="shrink-0 px-3 py-2 rounded-lg bg-violet-100 hover:bg-violet-200 transition-colors"
                                data-testid="copy-share-link"
                            >
                                {copied ? (
                                    <Check className="w-4 h-4 text-green-600" />
                                ) : (
                                    <Copy className="w-4 h-4 text-violet-600" />
                                )}
                            </button>
                        </div>
                        {copied && (
                            <p className="text-xs text-green-600 mt-1 font-medium">Enlace copiado</p>
                        )}
                    </div>
                </div>
                <div className="px-5 py-3 border-t border-slate-100 bg-slate-50">
                    <button
                        onClick={onClose}
                        className="w-full py-2 text-sm font-medium text-slate-600 hover:text-slate-800 transition-colors"
                    >
                        Cerrar
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
};

const ShareLinkSection = ({ link }) => {
    const [open, setOpen] = useState(false);

    return (
        <div className="px-4 py-3 border-b border-slate-200 bg-sky-50">
            <button
                onClick={() => setOpen(true)}
                className="w-full flex items-center gap-2 px-3 py-2 bg-violet-50 border border-violet-200 rounded-lg hover:bg-violet-100 transition-colors text-left"
                data-testid="open-share-link-btn"
            >
                <Link2 className="w-4 h-4 text-violet-600 shrink-0" />
                <span className="text-sm font-medium text-violet-700 truncate">Enlace para Compartir</span>
            </button>
            {open && <ShareLinkModal link={link} onClose={() => setOpen(false)} />}
        </div>
    );
};

export const Sidebar = ({ isOpen, onToggle }) => {
    const { user, logout } = useAuth();
    const navigate = useNavigate();

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    const handleNavClick = () => {
        // Close sidebar on mobile after navigation
        if (window.innerWidth < 768) {
            onToggle?.();
        }
    };

    const navItems = [
        {
            to: '/dashboard',
            icon: LayoutDashboard,
            label: 'Dashboard',
            roles: ['admin', 'supervisor', 'supervisor2'],  // Removed 'usuario'
            order: { default: 1 }
        },
        {
            to: '/dashboard/invoices',
            icon: FileText,
            label: 'Facturas',
            roles: ['admin', 'supervisor', 'supervisor2', 'usuario'],
            order: { usuario: 2, default: 2 }
        },
        {
            to: '/dashboard/upload',
            icon: Upload,
            label: 'Subir Factura',
            roles: ['admin', 'usuario'],
            order: { usuario: 1, default: 3 }
        },
        {
            to: '/dashboard/users',
            icon: Users,
            label: 'Usuarios',
            roles: ['admin', 'supervisor', 'supervisor2'],
            order: { default: 4 }
        },
        {
            to: '/dashboard/totals',
            icon: BarChart3,
            label: 'Totales',
            roles: ['admin', 'supervisor'],
            order: { default: 5 }
        },
        {
            to: '/dashboard/history',
            icon: History,
            label: 'Historial Admin',
            roles: ['admin', 'supervisor'],
            order: { default: 6 }
        },
        {
            to: '/dashboard/activities',
            icon: Activity,
            label: 'Registro Facturas',
            roles: ['admin', 'supervisor'],
            order: { default: 7 }
        },
        {
            to: '/dashboard/email-extractions',
            icon: Mail,
            label: 'Extracciones Correos',
            roles: ['admin', 'supervisor'],
            order: { default: 8 }
        },
        {
            to: '/dashboard/daily-deposits',
            icon: Landmark,
            label: 'Depositos del Dia',
            roles: ['admin'],
            order: { default: 9 }
        },
        {
            to: '/dashboard/stripe-history',
            icon: CreditCard,
            label: 'Historial Stripe',
            roles: ['admin'],
            order: { default: 10 }
        },
        {
            to: '/dashboard/extra-accounting',
            icon: Sparkles,
            label: 'Contabilidad Extra',
            roles: ['admin'],
            order: { default: 11 }
        },
        {
            to: '/dashboard/trash',
            icon: Trash2,
            label: 'Papelera',
            roles: ['admin'],
            order: { default: 12 }
        },
        {
            to: '/dashboard/paypal',
            icon: Wallet,
            label: 'Transacciones PayPal',
            roles: ['admin'],
            flag: 'is_paypal',
            order: { default: 6, usuario: 3 }
        }
    ];

    // Filter items by role (or special flag) and sort based on user role
    const filteredNavItems = navItems
        .filter(item => item.roles.includes(user?.role) || (item.flag && user?.[item.flag]))
        .sort((a, b) => {
            const orderA = a.order?.[user?.role] ?? a.order?.default ?? 99;
            const orderB = b.order?.[user?.role] ?? b.order?.default ?? 99;
            return orderA - orderB;
        });

    const getRoleBadgeClass = (role) => {
        switch (role) {
            case 'admin': return 'badge-admin';
            case 'supervisor': return 'badge-supervisor';
            case 'supervisor2': return 'badge-supervisor2';
            default: return 'badge-usuario';
        }
    };

    const getRoleLabel = (role) => {
        switch (role) {
            case 'admin': return 'Administrador';
            case 'supervisor': return 'Supervisor';
            case 'supervisor2': return 'Supervisor 2';
            default: return 'Usuario';
        }
    };

    return (
        <>
            {/* Mobile Overlay */}
            <div 
                className={`
                    fixed inset-0 bg-black/50 z-[55] md:hidden
                    transition-opacity duration-300
                    ${isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}
                `}
                onClick={onToggle}
            />
            
            {/* Sidebar */}
            <aside 
                className={`
                    fixed md:static top-0 left-0 h-screen h-[100dvh] md:h-screen w-64 
                    bg-sky-50 border-r border-slate-200
                    flex flex-col z-[60] md:z-50
                    transition-transform duration-300 ease-in-out
                    ${isOpen ? 'translate-x-0' : '-translate-x-full'}
                    md:translate-x-0
                `}
                data-testid="sidebar"
            >
                {/* Header */}
                <div className="p-6 border-b border-slate-200 bg-sky-50">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl bg-sky-500 flex items-center justify-center">
                                <FileCheck className="w-5 h-5 text-white" />
                            </div>
                            <div>
                                <h2 className="font-bold text-slate-800 font-['Manrope']">
                                    Contabilidad
                                </h2>
                                <p className="text-xs text-slate-500">Clara</p>
                            </div>
                        </div>
                        {/* Close button for mobile */}
                        <button
                            className="md:hidden p-2 rounded-lg hover:bg-sky-100 transition-colors"
                            onClick={onToggle}
                        >
                            <X className="w-5 h-5 text-slate-600" />
                        </button>
                    </div>
                </div>

                {/* User Info */}
                <div className="p-4 border-b border-slate-200 bg-sky-50">
                    <div className="flex items-center gap-3 p-3 bg-white rounded-xl shadow-sm">
                        <div className="w-10 h-10 rounded-full bg-sky-100 flex items-center justify-center">
                            <span className="text-sky-600 font-semibold">
                                {user?.name?.charAt(0)?.toUpperCase()}
                            </span>
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="font-medium text-slate-800 truncate text-sm">
                                {user?.name}
                            </p>
                            <span className={`badge ${getRoleBadgeClass(user?.role)} text-xs`}>
                                {getRoleLabel(user?.role)}
                            </span>
                        </div>
                    </div>
                </div>

                {/* Share Link */}
                {user?.share_link && (
                    <ShareLinkSection link={user.share_link} />
                )}

                {/* Bank categories reference (admin) */}
                {user?.role === 'admin' && <BankCategoriesSection onOpen={handleNavClick} />}
                {user?.role === 'admin' && <NotificationsPanel />}

                {/* Navigation */}
                <nav className="flex-1 p-4 space-y-1 overflow-y-auto bg-sky-50">
                    {filteredNavItems.map((item) => (
                        <NavLink
                            key={item.to}
                            to={item.to}
                            end={item.to === '/dashboard'}
                            className={({ isActive }) =>
                                `flex items-center gap-3 px-4 py-3 rounded-lg font-medium transition-colors
                                ${isActive 
                                    ? 'bg-sky-500 text-white' 
                                    : 'text-slate-600 hover:bg-sky-100 hover:text-sky-700'
                                }`
                            }
                            onClick={handleNavClick}
                            data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                        >
                            <item.icon className="w-5 h-5" />
                            <span>{item.label}</span>
                        </NavLink>
                    ))}
                </nav>

                {/* Logout */}
                <div className="p-4 pb-[max(1rem,env(safe-area-inset-bottom))] border-t border-slate-200 bg-sky-50 shrink-0">
                    <button
                        className="w-full flex items-center gap-3 px-4 py-3 rounded-lg font-medium text-slate-600 hover:text-red-600 hover:bg-red-50 transition-colors"
                        onClick={handleLogout}
                        data-testid="logout-btn"
                    >
                        <LogOut className="w-5 h-5" />
                        <span>Cerrar Sesión</span>
                    </button>
                </div>
            </aside>
        </>
    );
};

// Mobile menu button component
export const MobileMenuButton = ({ onClick }) => (
    <button
        className="p-2 rounded-lg hover:bg-slate-100 active:bg-slate-200 transition-colors"
        onClick={onClick}
        data-testid="mobile-menu-btn"
        type="button"
    >
        <Menu className="w-6 h-6 text-slate-700" />
    </button>
);
